/**
 * Converts a snake_case string to Title Case.
 * e.g., "still_looking" -> "Still Looking"
 * If there are no underscores, the string is returned with its first letter capitalized.
 */
export function formatSnakeCase(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Checks if a string looks like an ISO date (starts with YYYY-MM-DD).
 * e.g., "2026-02-18T15:39:04.632Z" -> true, "Honda CR-V" -> false
 */
export function isISODate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}/.test(value);
}

/**
 * Formats a date string as a user-friendly datetime.
 * Uses the browser's locale for regional formatting.
 * e.g., "Feb 18, 2026, 3:45 PM" (en-US) or "18 Feb 2026, 15:45" (en-GB)
 */
export function formatDateTime(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * Formats a date string as a user-friendly date (no time).
 * Uses the browser's locale for regional formatting.
 * e.g., "Feb 18, 2026" (en-US) or "18 Feb 2026" (en-GB)
 */
export function formatDate(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return date.toLocaleDateString(undefined, {
    dateStyle: 'medium',
  });
}

/**
 * Formats a duration in seconds to a human-readable string.
 * e.g., 90 -> "1m 30s", 45 -> "45s", null -> "—"
 */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
